// @ts-nocheck
/**
 * SensorParts.ts — Simulation logic for sensors, stepper motor, and NeoPixel devices.
 *
 * Implements:
 *  - tilt-switch
 *  - ntc-temperature-sensor
 *  - photodiode
 *  - gas-sensor (MQ-series)
 *  - flame-sensor
 *  - heart-beat-sensor
 *  - big-sound-sensor
 *  - small-sound-sensor
 *  - stepper-motor (NEMA full-step decode)
 *  - led-ring (WS2812B NeoPixel ring)
 *  - neopixel-matrix (WS2812B NeoPixel matrix)
 *  - pir-motion-sensor
 *  - hc-sr04
 */

import { PartSimulationRegistry } from "@/lib/velxio/simulation/parts/PartSimulationRegistry";
import {
  setAdcVoltage,
  emitPropertyChange,
} from "@/lib/velxio/simulation/parts/partUtils";
import {
  registerSensorUpdate,
  unregisterSensorUpdate,
} from "@/lib/velxio/simulation/SensorUpdateRegistry";

// ─── Tilt Switch ─────────────────────────────────────────────────────────────

/**
 * Tilt switch — click the element to toggle between tilted (OUT HIGH) and
 * upright (OUT LOW). Also controllable via SensorControlPanel "Toggle tilt" button.
 */
PartSimulationRegistry.register("tilt-switch", {
  attachEvents: (element, simulator, getArduinoPinHelper, componentId) => {
    const pin = getArduinoPinHelper("OUT");
    if (pin === null) return () => {};

    let tilted = false;

    const triggerToggle = () => {
      tilted = !tilted;
      simulator.setPinState(pin, tilted);
      emitPropertyChange(componentId, "tilted", tilted);
      console.log(
        `[TiltSwitch] pin ${pin} → ${tilted ? "HIGH (tilted)" : "LOW (upright)"}`,
      );
    };

    // Start LOW (upright)
    simulator.setPinState(pin, false);
    element.addEventListener("click", triggerToggle);

    // SensorControlPanel callback
    registerSensorUpdate(componentId, (values) => {
      if (values.toggle === true) triggerToggle();
    });

    return () => {
      element.removeEventListener("click", triggerToggle);
      unregisterSensorUpdate(componentId);
    };
  },
});

// ─── HDX Vibration Switch ────────────────────────────────────────────────────

PartSimulationRegistry.register("vibration-switch", {
  attachEvents: (element, simulator, getArduinoPinHelper, componentId) => {
    const pin = getArduinoPinHelper("OUT");
    if (pin === null) return () => {};

    let active = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const setActive = (value: boolean) => {
      active = value;
      (element as any).active = active;
      emitPropertyChange(componentId, "active", active);
      const activeHigh = (element as any).activeHigh ?? true;
      simulator.setPinState(pin, activeHigh ? active : !active);
    };

    const pulse = () => {
      setActive(true);
      if (timer) clearTimeout(timer);
      timer = setTimeout(
        () => setActive(false),
        Math.max(10, Number((element as any).pulseMs ?? 90)),
      );
    };

    const toggle = () => setActive(!active);

    simulator.setPinState(pin, false);
    element.addEventListener("click", pulse);

    registerSensorUpdate(componentId, (values) => {
      if (values.pulse === true) pulse();
      if (values.toggle === true) toggle();
    });

    return () => {
      element.removeEventListener("click", pulse);
      if (timer) clearTimeout(timer);
      unregisterSensorUpdate(componentId);
    };
  },
});

// ─── NTC Temperature Sensor ──────────────────────────────────────────────────

/**
 * NTC thermistor sensor — injects analog voltage representing temperature.
 * Default 25°C → 2.5V. SensorControlPanel slider adjusts temperature.
 *
 * Linear approximation: volts = clamp(2.5 - (temp - 25) * 0.02, 0, 5)
 * (25°C = 2.5V; lower temp = higher voltage, higher temp = lower voltage)
 */
PartSimulationRegistry.register("ntc-temperature-sensor", {
  attachEvents: (element, simulator, getArduinoPinHelper, componentId) => {
    const pin = getArduinoPinHelper("OUT");

    const tempToVolts = (temp: number) =>
      Math.max(0, Math.min(5, 2.5 - (temp - 25) * 0.02));

    // Room temperature default
    if (pin !== null) setAdcVoltage(simulator, pin, tempToVolts(25));
    (element as any).temperature = 25;

    const onInput = () => {
      const val = (element as any).value;
      if (val !== undefined && pin !== null) {
        setAdcVoltage(simulator, pin, (val / 1023.0) * 5.0);
      }
    };
    element.addEventListener("input", onInput);

    registerSensorUpdate(componentId, (values) => {
      if ("temperature" in values) {
        if (pin !== null) {
          setAdcVoltage(
            simulator,
            pin,
            tempToVolts(values.temperature as number),
          );
        }
        // Mirror to store — the SPICE ntc-temperature-sensor handler
        // reads comp.properties.temperature when computing R_ntc.
        emitPropertyChange(componentId, "temperature", values.temperature);
      }
    });

    return () => {
      element.removeEventListener("input", onInput);
      unregisterSensorUpdate(componentId);
    };
  },
});

// ─── LM35DZ Temperature Sensor ───────────────────────────────────────────────

PartSimulationRegistry.register("lm35dz", {
  attachEvents: (_element, simulator, getArduinoPinHelper, componentId) => {
    const pin = getArduinoPinHelper("OUT");
    const tempToVolts = (temp: number) =>
      Math.max(0, Math.min(1.5, temp * 0.01));

    if (pin !== null) setAdcVoltage(simulator, pin, tempToVolts(25));

    registerSensorUpdate(componentId, (values) => {
      if ("temperature" in values) {
        const temperature = values.temperature as number;
        (_element as any).temperature = temperature;
        if (pin !== null)
          setAdcVoltage(simulator, pin, tempToVolts(temperature));
        emitPropertyChange(componentId, "temperature", temperature);
      }
    });

    return () => unregisterSensorUpdate(componentId);
  },
});

// ─── Water Level Sensor ──────────────────────────────────────────────────────

// The analog output is driven through SPICE (see the `water-level-sensor`
// mapper in componentToSpice.ts): the panel slider updates the `level`
// property, the netlist rebuilds with a voltage source on the S net, and the
// ADC bridge reads that solved voltage. This handler only mirrors the value
// to the element (for the water-fill visual) and emits the property change.
PartSimulationRegistry.register("water-level-sensor", {
  attachEvents: (_element, _simulator, _getArduinoPinHelper, componentId) => {
    (_element as any).level = 0;

    registerSensorUpdate(componentId, (values) => {
      if ("level" in values) {
        const level = values.level as number;
        (_element as any).level = level;
        emitPropertyChange(componentId, "level", level);
      }
    });

    return () => unregisterSensorUpdate(componentId);
  },
});

// ─── Photodiode ──────────────────────────────────────────────────────────────

/**
 * Photodiode — 2-terminal passive, reverse-biased light sensor. The SPICE
 * emitter (componentToSpice.ts) reads `properties.lux` and drives a current
 * source (100 nA/lux). This handler wires the SensorControlPanel slider so
 * moving it updates the store → netlist rebuild → re-solve.
 */
PartSimulationRegistry.register("photodiode", {
  attachEvents: (_element, _simulator, _getArduinoPinHelper, componentId) => {
    registerSensorUpdate(componentId, (values) => {
      if ("lux" in values) {
        emitPropertyChange(componentId, "lux", values.lux);
      }
    });

    return () => {
      unregisterSensorUpdate(componentId);
    };
  },
});

// ─── Gas Sensor (MQ-series) ──────────────────────────────────────────────────

/**
 * Gas sensor — injects analog voltage on AOUT.
 * Default 1.5V (clean air / low gas). SensorControlPanel slider adjusts level (0–1023).
 * Higher value → higher voltage (more gas detected).
 */
PartSimulationRegistry.register("gas-sensor", {
  attachEvents: (element, simulator, getArduinoPinHelper, componentId) => {
    const pinAOUT = getArduinoPinHelper("AOUT");
    const pinDOUT = getArduinoPinHelper("DOUT");
    const pinManager = (simulator as any).pinManager;

    const el = element as any;
    el.ledPower = true;

    const unsubscribers: (() => void)[] = [];

    // Inject baseline analog voltage (1.5V ≈ clean air / low gas)
    if (pinAOUT !== null) {
      setAdcVoltage(simulator, pinAOUT, 1.5);
    }

    // DOUT from Arduino → threshold LED indicator
    if (pinDOUT !== null && pinManager) {
      unsubscribers.push(
        pinManager.onPinChange(pinDOUT, (_: number, state: boolean) => {
          el.ledD0 = state;
        }),
      );
    }

    // Allow element to update analog value if it fires input events
    const onInput = () => {
      const val = (el as any).value;
      if (val !== undefined && pinAOUT !== null) {
        setAdcVoltage(simulator, pinAOUT, (val / 1023.0) * 5.0);
      }
    };
    element.addEventListener("input", onInput);
    unsubscribers.push(() => element.removeEventListener("input", onInput));

    registerSensorUpdate(componentId, (values) => {
      if ("gasLevel" in values && pinAOUT !== null) {
        setAdcVoltage(
          simulator,
          pinAOUT,
          ((values.gasLevel as number) / 1023) * 5.0,
        );
      }
    });

    return () => {
      unsubscribers.forEach((u) => u());
      unregisterSensorUpdate(componentId);
    };
  },
});

// ─── Flame Sensor ────────────────────────────────────────────────────────────

/**
 * Flame sensor — injects analog voltage on AOUT.
 * Default 4.5V (no flame). SensorControlPanel slider: 0 = no flame (high V),
 * 1023 = intense flame (low V).
 */
PartSimulationRegistry.register("flame-sensor", {
  attachEvents: (element, simulator, getArduinoPinHelper, componentId) => {
    const pinAOUT = getArduinoPinHelper("AOUT");
    const pinDOUT = getArduinoPinHelper("DOUT");
    const pinManager = (simulator as any).pinManager;

    const el = element as any;
    el.ledPower = true;

    const unsubscribers: (() => void)[] = [];

    if (pinAOUT !== null) {
      setAdcVoltage(simulator, pinAOUT, 5.0); // no flame = high voltage
    }

    if (pinDOUT !== null && pinManager) {
      unsubscribers.push(
        pinManager.onPinChange(pinDOUT, (_: number, state: boolean) => {
          el.ledSignal = state;
        }),
      );
    }

    const onInput = () => {
      const val = (el as any).value;
      if (val !== undefined && pinAOUT !== null) {
        // Flame sensor output is inverted: higher value = flame = lower volts.
        // Match the SensorControlPanel formula below so both paths agree.
        setAdcVoltage(simulator, pinAOUT, 5.0 - (val / 1023.0) * 5.0);
      }
    };
    element.addEventListener("input", onInput);
    unsubscribers.push(() => element.removeEventListener("input", onInput));

    registerSensorUpdate(componentId, (values) => {
      if ("intensity" in values && pinAOUT !== null) {
        // 0 = no flame → high voltage (4.5V); 1023 = flame → low voltage (0.2V)
        const volts = 5.0 - ((values.intensity as number) / 1023) * 5.0;
        setAdcVoltage(simulator, pinAOUT, volts);
      }
    });

    return () => {
      unsubscribers.forEach((u) => u());
      unregisterSensorUpdate(componentId);
    };
  },
});

// ─── Heart Beat Sensor ───────────────────────────────────────────────────────

/**
 * Heart beat sensor — simulates a 60 BPM signal on OUT pin.
 * Every 1000ms: briefly pulls OUT HIGH for 100ms, then LOW again.
 */
PartSimulationRegistry.register("heart-beat-sensor", {
  attachEvents: (element, simulator, getArduinoPinHelper) => {
    const pin = getArduinoPinHelper("OUT");
    if (pin === null) return () => {};

    simulator.setPinState(pin, false);

    const intervalId = setInterval(() => {
      simulator.setPinState(pin, true); // pulse HIGH
      setTimeout(() => simulator.setPinState(pin, false), 100);
    }, 1000);

    return () => clearInterval(intervalId);
  },
});

// ─── Big Sound Sensor ────────────────────────────────────────────────────────

/**
 * Big sound sensor (FC-04) — injects mid-range analog on AOUT.
 * SensorControlPanel slider adjusts sound level (0–1023).
 */
PartSimulationRegistry.register("big-sound-sensor", {
  attachEvents: (element, simulator, getArduinoPinHelper, componentId) => {
    const pinAOUT = getArduinoPinHelper("AOUT");
    const pinDOUT = getArduinoPinHelper("DOUT");
    const pinManager = (simulator as any).pinManager;

    const el = element as any;
    el.led2 = true; // Power LED

    const unsubscribers: (() => void)[] = [];

    if (pinAOUT !== null) {
      setAdcVoltage(simulator, pinAOUT, 2.5);
    }

    if (pinDOUT !== null && pinManager) {
      unsubscribers.push(
        pinManager.onPinChange(pinDOUT, (_: number, state: boolean) => {
          el.led1 = state;
        }),
      );
    }

    const onInput = () => {
      const val = (el as any).value;
      if (val !== undefined && pinAOUT !== null) {
        setAdcVoltage(simulator, pinAOUT, (val / 1023.0) * 5.0);
      }
    };
    element.addEventListener("input", onInput);
    unsubscribers.push(() => element.removeEventListener("input", onInput));

    registerSensorUpdate(componentId, (values) => {
      if ("soundLevel" in values && pinAOUT !== null) {
        setAdcVoltage(
          simulator,
          pinAOUT,
          ((values.soundLevel as number) / 1023) * 5.0,
        );
      }
    });

    return () => {
      unsubscribers.forEach((u) => u());
      unregisterSensorUpdate(componentId);
    };
  },
});

// ─── Small Sound Sensor ──────────────────────────────────────────────────────

/**
 * Small sound sensor (KY-038) — injects mid-range analog on AOUT.
 * SensorControlPanel slider adjusts sound level (0–1023).
 */
PartSimulationRegistry.register("small-sound-sensor", {
  attachEvents: (element, simulator, getArduinoPinHelper, componentId) => {
    const pinAOUT = getArduinoPinHelper("AOUT");
    const pinDOUT = getArduinoPinHelper("DOUT");
    const pinManager = (simulator as any).pinManager;

    const el = element as any;
    el.ledPower = true;

    const unsubscribers: (() => void)[] = [];

    if (pinAOUT !== null) {
      setAdcVoltage(simulator, pinAOUT, 2.5);
    }

    if (pinDOUT !== null && pinManager) {
      unsubscribers.push(
        pinManager.onPinChange(pinDOUT, (_: number, state: boolean) => {
          el.ledSignal = state;
        }),
      );
    }

    const onInput = () => {
      const val = (el as any).value;
      if (val !== undefined && pinAOUT !== null) {
        setAdcVoltage(simulator, pinAOUT, (val / 1023.0) * 5.0);
      }
    };
    element.addEventListener("input", onInput);
    unsubscribers.push(() => element.removeEventListener("input", onInput));

    registerSensorUpdate(componentId, (values) => {
      if ("soundLevel" in values && pinAOUT !== null) {
        setAdcVoltage(
          simulator,
          pinAOUT,
          ((values.soundLevel as number) / 1023) * 5.0,
        );
      }
    });

    return () => {
      unsubscribers.forEach((u) => u());
      unregisterSensorUpdate(componentId);
    };
  },
});

// ─── Stepper Motor (28BYJ-48 unipolar / legacy NEMA decode) ──────────────────

/**
 * Stepper motor — the kit's 28BYJ-48 geared unipolar stepper (coil pins
 * A/B/C/D, usually driven through the ULN2003 module; 2048 full steps per
 * output-shaft revolution). Wires named with the legacy wokwi NEMA terminals
 * (A-, A+, B+, B-) are still decoded so old projects and the GPIO/A4988
 * examples keep rotating; those step at the NEMA 1.8°.
 *
 * Both decoders track the magnetic-field electrical angle instead of
 * matching a fixed coil table. The rotor follows the net field vector, so
 * this works for ANY drive mode the firmware (or a driver) produces:
 * wave-drive (one coil), full-step two-phase (two coils), or half-step.
 */
PartSimulationRegistry.register("stepper-motor", {
  attachEvents: (element, simulator, getArduinoPinHelper) => {
    const pinManager = (simulator as any).pinManager;
    if (!pinManager) return () => {};

    const el = element as any;
    // 28BYJ-48: 32 rotor steps × 64:1 gearbox = 2048 steps per revolution.
    const STEP_ANGLE_28BYJ = 360 / 2048;
    const STEP_ANGLE_NEMA = 1.8;

    let cumAngle = el.angle ?? 0;
    let prevField = Number.NaN; // previous field angle in radians, NaN = unset

    function onFieldChange(a: number, b: number, stepAngle: number) {
      if (a === 0 && b === 0) return; // no field → rotor holds position

      const field = Math.atan2(b, a); // electrical angle of the field vector
      if (Number.isNaN(prevField)) {
        prevField = field;
        return;
      }
      // Shortest signed rotation between the two field angles.
      let delta = field - prevField;
      while (delta > Math.PI) delta -= 2 * Math.PI;
      while (delta < -Math.PI) delta += 2 * Math.PI;
      prevField = field;

      // One quarter electrical turn (90°, π/2 rad) = one full mechanical step.
      cumAngle += (delta / (Math.PI / 2)) * stepAngle;
      el.angle = ((cumAngle % 360) + 360) % 360;
    }

    const unsubscribers: (() => void)[] = [];
    const subscribe = (pinName: string, onState: (s: boolean) => void) => {
      const pin = getArduinoPinHelper(pinName);
      if (pin === null) return;
      unsubscribers.push(
        pinManager.onPinChange(pin, (_: number, s: boolean) => onState(s)),
      );
    };

    // 28BYJ-48 phases at electrical 0° / 90° / 180° / 270°.
    const phases = { a: false, b: false, c: false, d: false };
    const onPhaseChange = () =>
      onFieldChange(
        (phases.a ? 1 : 0) - (phases.c ? 1 : 0),
        (phases.b ? 1 : 0) - (phases.d ? 1 : 0),
        STEP_ANGLE_28BYJ,
      );
    subscribe("A", (s) => {
      phases.a = s;
      onPhaseChange();
    });
    subscribe("B", (s) => {
      phases.b = s;
      onPhaseChange();
    });
    subscribe("C", (s) => {
      phases.c = s;
      onPhaseChange();
    });
    subscribe("D", (s) => {
      phases.d = s;
      onPhaseChange();
    });

    // Legacy bipolar terminal pairs (wokwi-stepper-motor pin names).
    const coils = { aMinus: false, aPlus: false, bPlus: false, bMinus: false };
    const onCoilChange = () =>
      onFieldChange(
        (coils.aPlus ? 1 : 0) - (coils.aMinus ? 1 : 0),
        (coils.bPlus ? 1 : 0) - (coils.bMinus ? 1 : 0),
        STEP_ANGLE_NEMA,
      );
    subscribe("A-", (s) => {
      coils.aMinus = s;
      onCoilChange();
    });
    subscribe("A+", (s) => {
      coils.aPlus = s;
      onCoilChange();
    });
    subscribe("B+", (s) => {
      coils.bPlus = s;
      onCoilChange();
    });
    subscribe("B-", (s) => {
      coils.bMinus = s;
      onCoilChange();
    });

    return () => {
      for (const unsubscribe of unsubscribers) unsubscribe();
    };
  },
});

// ─── ULN2003 Stepper Driver Module ───────────────────────────────────────────

PartSimulationRegistry.register("uln2003-driver", {
  attachEvents: (element, simulator, getArduinoPinHelper) => {
    const pinManager = (simulator as any).pinManager;
    if (!pinManager) return () => {};

    const pairs = [
      ["IN1", "A"],
      ["IN2", "B"],
      ["IN3", "C"],
      ["IN4", "D"],
    ];

    const unsubscribers: (() => void)[] = [];
    const el = element as any;

    for (const [inputName, outputName] of pairs) {
      const inputPin = getArduinoPinHelper(inputName);
      const outputPin = getArduinoPinHelper(outputName);
      if (inputPin === null) continue;

      unsubscribers.push(
        pinManager.onPinChange(inputPin, (_: number, state: boolean) => {
          el[inputName.toLowerCase()] = state;
          if (outputPin !== null) simulator.setPinState(outputPin, state);
        }),
      );
    }

    return () => {
      for (const unsubscribe of unsubscribers) unsubscribe();
    };
  },
});

// ─── WS2812B NeoPixel decode helper ──────────────────────────────────────────

/**
 * Decode WS2812B bit-stream from DIN pin changes for NeoPixel devices.
 */
function createNeopixelDecoder(
  simulator: any,
  pinDIN: number,
  onPixel: (index: number, r: number, g: number, b: number) => void,
): () => void {
  const pinManager = simulator.pinManager;
  if (!pinManager) return () => {};

  const RESET_CYCLES = 800;
  const BIT1_THRESHOLD = 8;

  let lastRisingCycle = 0;
  let lastFallingCycle = 0;
  let lastHigh = false;

  let bitBuf = 0;
  let bitsCollected = 0;
  let byteBuf: number[] = [];
  let pixelIndex = 0;

  const unsub = pinManager.onPinChange(pinDIN, (_: number, high: boolean) => {
    const cpu = simulator.cpu ?? (simulator as any).cpu;
    const now: number = cpu?.cycles ?? 0;

    if (high) {
      const lowDur = now - lastFallingCycle;
      if (lowDur > RESET_CYCLES) {
        pixelIndex = 0;
        byteBuf = [];
        bitBuf = 0;
        bitsCollected = 0;
      }
      lastRisingCycle = now;
      lastHigh = true;
    } else {
      if (lastHigh) {
        const highDur = now - lastRisingCycle;
        const bit = highDur > BIT1_THRESHOLD ? 1 : 0;

        bitBuf = (bitBuf << 1) | bit;
        bitsCollected++;

        if (bitsCollected === 8) {
          byteBuf.push(bitBuf & 0xff);
          bitBuf = 0;
          bitsCollected = 0;

          if (byteBuf.length === 3) {
            const g = byteBuf[0];
            const r = byteBuf[1];
            const b = byteBuf[2];
            onPixel(pixelIndex++, r, g, b);
            byteBuf = [];
          }
        }
      }
      lastFallingCycle = now;
      lastHigh = false;
    }
  });

  return unsub;
}

// ─── LED Ring (WS2812B NeoPixel ring) ────────────────────────────────────────

PartSimulationRegistry.register("led-ring", {
  attachEvents: (element, simulator, getArduinoPinHelper) => {
    const pinDIN = getArduinoPinHelper("DIN");
    if (pinDIN === null) return () => {};

    const el = element as any;

    const unsub = createNeopixelDecoder(
      simulator as any,
      pinDIN,
      (index, r, g, b) => {
        try {
          el.setPixel(index, { r, g, b });
        } catch (_) {
          // setPixel not yet available (element not upgraded) — ignore
        }
      },
    );

    return unsub;
  },
});

// ─── NeoPixel Matrix (WS2812B matrix grid) ────────────────────────────────────

PartSimulationRegistry.register("neopixel-matrix", {
  attachEvents: (element, simulator, getArduinoPinHelper) => {
    const pinDIN = getArduinoPinHelper("DIN");
    if (pinDIN === null) return () => {};

    const el = element as any;

    const unsub = createNeopixelDecoder(
      simulator as any,
      pinDIN,
      (index, r, g, b) => {
        const cols: number = el.cols ?? 8;
        const row = Math.floor(index / cols);
        const col = index % cols;
        try {
          el.setPixel(row, col, { r, g, b });
        } catch (_) {
          // ignore
        }
      },
    );

    return unsub;
  },
});

// ─── Single NeoPixel (WS2812B) ───────────────────────────────────────────────

/**
 * Single addressable RGB LED — decodes the WS2812B data stream on DIN.
 */
PartSimulationRegistry.register("neopixel", {
  attachEvents: (element, simulator, getArduinoPinHelper) => {
    const pinDIN = getArduinoPinHelper("DIN");
    if (pinDIN === null) return () => {};

    const el = element as any;

    const unsub = createNeopixelDecoder(
      simulator as any,
      pinDIN,
      (_index, r, g, b) => {
        el.r = r / 255;
        el.g = g / 255;
        el.b = b / 255;
      },
    );

    return unsub;
  },
});

// ─── PIR Motion Sensor ───────────────────────────────────────────────────────

/**
 * PIR motion sensor — click the element OR press "Simulate motion" in the
 * SensorControlPanel to trigger a 3-second HIGH pulse on OUT.
 */
PartSimulationRegistry.register("pir-motion-sensor", {
  attachEvents: (element, simulator, getArduinoPinHelper, componentId) => {
    const pin = getArduinoPinHelper("OUT");
    if (pin === null) return () => {};

    simulator.setPinState(pin, false); // idle LOW

    let timer: ReturnType<typeof setTimeout> | null = null;

    const triggerMotion = () => {
      if (timer !== null) clearTimeout(timer);
      simulator.setPinState(pin, true);
      console.log("[PIR] Motion detected → OUT HIGH");
      timer = setTimeout(() => {
        simulator.setPinState(pin, false);
        timer = null;
        console.log("[PIR] Motion ended → OUT LOW");
      }, 3000);
    };

    element.addEventListener("click", triggerMotion);

    registerSensorUpdate(componentId, (values) => {
      if (values.trigger === true) triggerMotion();
    });

    return () => {
      element.removeEventListener("click", triggerMotion);
      if (timer !== null) clearTimeout(timer);
      unregisterSensorUpdate(componentId);
    };
  },
});

// ─── KS2E-M-DC5 Relay ────────────────────────────────────────────────────────

/**
 * Dual-coil relay — listens for COIL1/COIL2 pin state changes.
 */
PartSimulationRegistry.register("ks2e-m-dc5", {
  onPinStateChange: (pinName, state, _element) => {
    if (pinName === "COIL1" || pinName === "COIL2") {
      console.log(
        `[Relay KS2E] ${pinName} → ${state ? "ACTIVATED" : "RELEASED"}`,
      );
    }
  },
});

// ─── HC-SR04 Ultrasonic Distance Sensor ──────────────────────────────────────

/**
 * Ultrasonic sensor — monitors the TRIG pin.
 * When TRIG goes HIGH, responds with an ECHO HIGH pulse whose duration
 * encodes the configured distance (default 10 cm).
 *
 * Echo timing: echoMs = distanceCm / 17.15
 * (speed of sound ~343 m/s; round-trip halves: 17150 cm/s)
 */
PartSimulationRegistry.register("hc-sr04", {
  attachEvents: (element, simulator, getArduinoPinHelper, componentId) => {
    const trigPin = getArduinoPinHelper("TRIG");
    const echoPin = getArduinoPinHelper("ECHO");
    if (trigPin === null || echoPin === null) return () => {};

    const el = element as any;
    let distanceCm = parseFloat(el.distance) || 10; // default distance in cm

    // ── ESP32 path: delegate protocol to backend QEMU worker ──

    const handledNatively =
      typeof (simulator as any).registerSensor === "function" &&
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (simulator as any).registerSensor("hc-sr04", trigPin, {
        distance: distanceCm,
        echo_pin: echoPin,
      });

    if (handledNatively) {
      registerSensorUpdate(componentId, (values) => {
        if ("distance" in values) {
          distanceCm = Math.max(2, Math.min(400, values.distance as number));
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (simulator as any).updateSensor(trigPin, {
          distance: distanceCm,
          echo_pin: echoPin,
        });
      });

      return () => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (simulator as any).unregisterSensor(trigPin);
        unregisterSensorUpdate(componentId);
      };
    }

    // ── AVR / RP2040 path: local pin scheduling ──
    simulator.setPinState(echoPin, false); // ECHO LOW initially

    const cleanup = simulator.pinManager.onPinChange(
      trigPin,
      (_: number, state: boolean) => {
        if (!state) return; // only react on TRIG HIGH
        if (typeof simulator.schedulePinChange === "function") {
          const clockHz: number =
            typeof (simulator as any).getClockHz === "function"
              ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (simulator as any).getClockHz()
              : 16_000_000;
          const now = simulator.getCurrentCycles() as number;
          const processingCycles = Math.round(600e-6 * clockHz); // 600 µs sensor overhead
          const echoCycles = Math.round((distanceCm / 17150) * clockHz);
          simulator.schedulePinChange(echoPin, true, now + processingCycles);
          simulator.schedulePinChange(
            echoPin,
            false,
            now + processingCycles + echoCycles,
          );
          console.log(
            `[HC-SR04] Scheduled ECHO (${distanceCm} cm, echo=${(echoCycles / (clockHz / 1e6)).toFixed(1)} µs)`,
          );
        } else {
          // Fallback: best-effort async (works with delay()-based sketches, not pulseIn)
          const echoMs = Math.max(1, distanceCm / 17.15);
          setTimeout(() => {
            simulator.setPinState(echoPin, true);
            setTimeout(() => {
              simulator.setPinState(echoPin, false);
            }, echoMs);
          }, 1);
        }
      },
    );

    registerSensorUpdate(componentId, (values) => {
      if ("distance" in values) {
        distanceCm = Math.max(2, Math.min(400, values.distance as number));
      }
    });

    return () => {
      cleanup();
      unregisterSensorUpdate(componentId);
    };
  },
});
